import { X } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { ToastContext } from "./Toast.context";
import type { ToastContextValue, ToastInput, ToastManagerProps, ToastPlacement, ToastProviderProps, ToastRecord, ToastTone } from "./Toast.types";

const placementClassNames: Record<ToastPlacement, string> = {
  "top-left": "bfrs-left-4 bfrs-top-4 bfrs-items-start",
  "top-center": "bfrs-left-1/2 bfrs-top-4 -bfrs-translate-x-1/2 bfrs-items-center",
  "top-right": "bfrs-right-4 bfrs-top-4 bfrs-items-end",
  "bottom-left": "bfrs-bottom-4 bfrs-left-4 bfrs-items-start",
  "bottom-center": "bfrs-bottom-5 bfrs-left-1/2 -bfrs-translate-x-1/2 bfrs-items-center",
  "bottom-right": "bfrs-bottom-4 bfrs-right-4 bfrs-items-end"
};

const toneBorderClassNames: Record<ToastTone, string> = {
  neutral: "bfrs-border-l-primary",
  success: "bfrs-border-l-success",
  warning: "bfrs-border-l-warning",
  danger: "bfrs-border-l-danger",
  info: "bfrs-border-l-info"
};

const toneIconClassNames: Record<ToastTone, string> = {
  neutral: "bfrs-bg-primary",
  success: "bfrs-bg-success",
  warning: "bfrs-bg-warning",
  danger: "bfrs-bg-danger",
  info: "bfrs-bg-info"
};

const toneIconGlyph: Record<ToastTone, string> = {
  neutral: "i",
  success: "✓",
  warning: "!",
  danger: "!",
  info: "i"
};

function normalizeToast(input: ToastInput, defaultDuration: number): Omit<ToastRecord, "id"> & { id?: string } {
  if (typeof input === "string") {
    return {
      title: input,
      tone: "neutral",
      duration: defaultDuration,
      dismissible: true
    };
  }

  return {
    tone: "neutral",
    duration: defaultDuration,
    dismissible: true,
    ...input
  };
}

function withTone(tone: ToastTone, input: string | Omit<Extract<ToastInput, object>, "tone">): ToastInput {
  return typeof input === "string" ? { title: input, tone } : { ...input, tone };
}

function DefaultToastIcon({ tone }: { tone: ToastTone }) {
  return (
    <span className={cn("bfrs-flex bfrs-size-5 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-text-[12px] bfrs-font-bold bfrs-leading-none bfrs-text-white", toneIconClassNames[tone])} aria-hidden="true">
      {toneIconGlyph[tone]}
    </span>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (toast.duration <= 0 || !Number.isFinite(toast.duration)) return undefined;

    const timeout = window.setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.duration, toast.id]);

  const role = toast.tone === "danger" ? "alert" : "status";

  return (
    <div
      role={role}
      aria-live={role === "alert" ? "assertive" : "polite"}
      className={cn(
        "bfrs-pointer-events-auto bfrs-flex bfrs-w-full bfrs-min-w-[300px] bfrs-max-w-[400px] bfrs-items-start bfrs-gap-2.5 bfrs-rounded-[10px] bfrs-border bfrs-border-l-[3px] bfrs-border-border bfrs-bg-surface-card bfrs-px-[14px] bfrs-py-3 bfrs-text-sm bfrs-text-text-primary bfrs-shadow-elevated bfrs-outline-none bfrs-animate-content-in",
        toneBorderClassNames[toast.tone],
        toast.className
      )}
    >
      {toast.icon ?? <DefaultToastIcon tone={toast.tone} />}
      <div className="bfrs-min-w-0 bfrs-flex-1">
        <div className="bfrs-text-[13px] bfrs-font-semibold bfrs-leading-5 bfrs-text-text-primary">{toast.title}</div>
        {toast.description && <div className="bfrs-mt-0.5 bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{toast.description}</div>}
      </div>
      {toast.action && <div className="bfrs-ml-1 bfrs-shrink-0">{toast.action}</div>}
      {toast.dismissible && (
        <button
          type="button"
          className="-bfrs-mr-1 bfrs-flex bfrs-size-6 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-md bfrs-text-text-secondary bfrs-outline-none bfrs-transition hover:bfrs-bg-surface-muted hover:bfrs-text-text-primary focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/50"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
        >
          <X size={13} weight="bold" />
        </button>
      )}
    </div>
  );
}

export function ToastManager({ toasts, onDismiss, placement = "bottom-center", className }: ToastManagerProps) {
  if (toasts.length === 0) return null;

  const stack = (
    // Portaled to <body> so the fixed, z-[9999] stack escapes any ancestor
    // stacking context (a transformed/opacity/filtered parent would otherwise
    // trap it and let page content paint over the toasts). BfrsPortalScope
    // re-applies the .bfrs token scope at the portal root.
    <BfrsPortalScope>
      <div className={cn("bfrs-pointer-events-none bfrs-fixed bfrs-z-[2147483646] bfrs-flex bfrs-w-[calc(100vw-2rem)] bfrs-max-w-[400px] bfrs-flex-col bfrs-gap-2.5", placementClassNames[placement], className)}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </BfrsPortalScope>
  );

  if (typeof document === "undefined") return stack;
  return createPortal(stack, document.body);
}

export function ToastProvider({ children, placement = "bottom-center", maxToasts = 4, defaultDuration = 2600, className }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idCounterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const clear = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const normalized = normalizeToast(input, defaultDuration);
      const id = normalized.id ?? `toast-${Date.now()}-${idCounterRef.current++}`;
      const nextToast: ToastRecord = { ...normalized, id };

      setToasts((current) => [...current.filter((item) => item.id !== id), nextToast].slice(-maxToasts));
      return id;
    },
    [defaultDuration, maxToasts]
  );

  const success = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("success", input)), [toast]);
  const warning = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("warning", input)), [toast]);
  const danger = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("danger", input)), [toast]);
  const info = useCallback((input: string | Omit<Extract<ToastInput, object>, "tone">) => toast(withTone("info", input)), [toast]);

  const contextValue = useMemo<ToastContextValue>(
    () => ({ toasts, toast, success, warning, danger, info, dismiss, clear }),
    [clear, danger, dismiss, info, success, toast, toasts, warning]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastManager toasts={toasts} onDismiss={dismiss} placement={placement} className={className} />
    </ToastContext.Provider>
  );
}
